import { defineStore } from "pinia";
import { computed, ref } from "vue";

import ingestionApi, {
  type ApproveAllRequest,
  type ApproveFieldDriftRequest,
  type ApproveFieldsBatchRequest,
  type ApproveRoutingRequest,
  type ApproveStructureRequest,
  type CreateSourceRequest,
  type CreateSourceResponse,
  type FailureItem,
  type FieldDecision,
  type IngestionSummary,
  type InboxLanding,
  type RoutingDecision,
  type SourceSummary,
  type StructureDecision,
} from "@/api/admin/ingestion";

export const useIngestionStore = defineStore("ingestion", () => {
  const sources = ref<SourceSummary[]>([]);
  const summary = ref<IngestionSummary | null>(null);
  const landing = ref<InboxLanding | null>(null);
  // The four queues, fetched separately (§2). Each holds ALL sources' decisions;
  // active-source narrowing happens in getters.
  const structureQueue = ref<StructureDecision[]>([]);
  const routingQueue = ref<RoutingDecision[]>([]);
  const fieldDriftQueue = ref<FieldDecision[]>([]);
  const failuresQueue = ref<FailureItem[]>([]);
  const activeSourceId = ref<null | string>(null); // null = all sources (crmConfigId)
  const isLoading = ref(false);
  const error = ref<null | string>(null);

  // Client-side active-source filter for each queue.
  const filteredStructure = computed(() =>
    activeSourceId.value
      ? structureQueue.value.filter((d) => d.crmConfigId === activeSourceId.value)
      : structureQueue.value
  );
  const filteredRouting = computed(() =>
    activeSourceId.value
      ? routingQueue.value.filter((d) => d.crmConfigId === activeSourceId.value)
      : routingQueue.value
  );
  const filteredFieldDrift = computed(() =>
    activeSourceId.value
      ? fieldDriftQueue.value.filter((d) => d.crmConfigId === activeSourceId.value)
      : fieldDriftQueue.value
  );
  // FailureItem has no crmConfigId; failures are not source-filtered client-side.
  const filteredFailures = computed(() => failuresQueue.value);

  // Total badge count for the sidebar — the four filtered queue lengths.
  const totalDecisionCount = computed(
    () =>
      filteredStructure.value.length +
      filteredRouting.value.length +
      filteredFieldDrift.value.length +
      filteredFailures.value.length
  );

  // Per-queue counts for the Review landing summary (mirror the four queues).
  const decisionCounts = computed(() => ({
    failures: filteredFailures.value.length,
    field: filteredFieldDrift.value.length,
    routing: filteredRouting.value.length,
    structure: filteredStructure.value.length,
  }));

  // Filter the hero by active source, then sort so the campaigns with the most
  // outstanding work surface first.
  const reviewByCampaign = computed(() => {
    const rows = summary.value?.reviewByCampaign ?? [];
    const scoped = activeSourceId.value
      ? rows.filter((r) => r.crmConfigId === activeSourceId.value)
      : rows;
    return [...scoped].sort(
      (a, b) =>
        b.structureCount +
        b.fieldCount +
        b.routingCount +
        b.failureCount -
        (a.structureCount + a.fieldCount + a.routingCount + a.failureCount)
    );
  });

  // Distinct sources with outstanding ISSUES to map, for the sidebar "Data Sources"
  // badge + Admin parent dot. An issue is a decision a human resolves — a new column,
  // routing value, or data format. FAILURES are deliberately excluded (they're a
  // separate notification), so the badge agrees with the overview banner and the
  // per-source "Map N items" button, all of which count issues only.
  const sourcesNeedingMappingCount = computed(() => {
    const ids = new Set<string>();
    for (const r of summary.value?.reviewByCampaign ?? []) {
      if (r.structureCount + r.fieldCount + r.routingCount > 0) {
        ids.add(r.crmConfigId);
      }
    }
    return ids.size;
  });

  const isAllCaughtUp = computed(() => totalDecisionCount.value === 0);

  // c2 gate: true while any in-scope source still has routing/field decisions whose AI
  // verdict hasn't landed (`aiStatus === "PENDING"`). The Review landing uses this to show
  // "analyzing" and withhold the Start button until the AI has run fully.
  const aiPending = computed(() => reviewByCampaign.value.some((r) => r.aiStatus === "PENDING"));

  // Bumped by $reset. A load still in flight at sign-out carries the previous org's queues and
  // sources, so every loader drops its result once the generation has moved on.
  let generation = 0;

  async function loadSources() {
    const requestGeneration = generation;
    const result = await ingestionApi.listSources();
    if (requestGeneration === generation) sources.value = result;
  }

  async function loadSummary() {
    const requestGeneration = generation;
    isLoading.value = true;
    error.value = null;
    try {
      const result = await ingestionApi.getSummary();
      if (requestGeneration === generation) summary.value = result;
    } catch (e) {
      if (requestGeneration === generation) {
        error.value = e instanceof Error ? e.message : "Failed to load summary";
      }
    } finally {
      if (requestGeneration === generation) isLoading.value = false;
    }
  }

  // Fetch the four queues + landing rollup in parallel. Swallows errors into
  // `error` (like loadSummary) so a failed inbox GET never rejects refreshInbox
  // — callers (layout onMounted, post-approve handlers) stay in a sane state.
  async function loadQueues() {
    const requestGeneration = generation;
    try {
      const [structure, routing, fieldDrift, failures, landingRollup] = await Promise.all([
        ingestionApi.getStructureQueue(),
        ingestionApi.getRoutingQueue(),
        ingestionApi.getFieldDriftQueue(),
        ingestionApi.getFailuresQueue(),
        ingestionApi.getLanding(),
      ]);
      if (requestGeneration !== generation) return;
      structureQueue.value = structure;
      routingQueue.value = routing;
      fieldDriftQueue.value = fieldDrift;
      failuresQueue.value = failures;
      landing.value = landingRollup;
    } catch (e) {
      if (requestGeneration !== generation) return;
      error.value = e instanceof Error ? e.message : "Failed to load inbox";
    }
  }

  // Lean single-queue loaders for the connect/edit wizards. The Identify step reads only
  // `filteredStructure`; the Map step reads only `filteredFieldDrift`. Loading just the one
  // queue a step needs — instead of the full 6-endpoint refreshInbox (summary + 4 queues +
  // landing) — avoids wasted round-trips AND shrinks the blast radius for a stalled DB
  // connection: the upload→Identify poll fires ONE request, not six, so a single dropped
  // connection can't deadlock the whole `Promise.all` (see RAP-325 perf investigation).
  // Non-fatal, like loadQueues.
  async function loadStructureQueue() {
    const requestGeneration = generation;
    try {
      const result = await ingestionApi.getStructureQueue();
      if (requestGeneration === generation) structureQueue.value = result;
    } catch (e) {
      if (requestGeneration !== generation) return;
      error.value = e instanceof Error ? e.message : "Failed to load structure queue";
    }
  }
  async function loadFieldDriftQueue() {
    const requestGeneration = generation;
    try {
      const result = await ingestionApi.getFieldDriftQueue();
      if (requestGeneration === generation) fieldDriftQueue.value = result;
    } catch (e) {
      if (requestGeneration !== generation) return;
      error.value = e instanceof Error ? e.message : "Failed to load field-mapping queue";
    }
  }
  async function loadRoutingQueue() {
    const requestGeneration = generation;
    try {
      const result = await ingestionApi.getRoutingQueue();
      if (requestGeneration === generation) routingQueue.value = result;
    } catch (e) {
      if (requestGeneration !== generation) return;
      error.value = e instanceof Error ? e.message : "Failed to load routing queue";
    }
  }

  /** A setup store has no working Pinia `$reset`; sign-out (`clearAllStores`) needs this one. */
  function $reset() {
    generation++;
    sources.value = [];
    summary.value = null;
    landing.value = null;
    structureQueue.value = [];
    routingQueue.value = [];
    fieldDriftQueue.value = [];
    failuresQueue.value = [];
    activeSourceId.value = null;
    isLoading.value = false;
    error.value = null;
  }

  function setActiveSource(id: null | string) {
    activeSourceId.value = id;
  }

  async function createSource(body: CreateSourceRequest): Promise<CreateSourceResponse> {
    const res = await ingestionApi.createSource(body);
    await loadSources();
    return res;
  }

  // After any approval, refresh summary + queues so counts stay truthful.
  async function approveStructure(body: ApproveStructureRequest) {
    await ingestionApi.approveStructure(body);
    await refreshInbox();
  }
  async function approveFieldDrift(body: ApproveFieldDriftRequest) {
    await ingestionApi.approveFieldDrift(body);
    await refreshInbox();
  }
  async function approveFieldsBatch(body: ApproveFieldsBatchRequest) {
    const res = await ingestionApi.approveFieldsBatch(body);
    await refreshInbox();
    return res;
  }
  async function approveRouting(body: ApproveRoutingRequest) {
    await ingestionApi.approveRouting(body);
    await refreshInbox();
  }
  async function approveAll(body: ApproveAllRequest) {
    const res = await ingestionApi.approveAll(body);
    await refreshInbox();
    return res;
  }
  async function retryRecord(recordId: string) {
    await ingestionApi.retryRecord(recordId);
    await refreshInbox();
  }
  async function reprocessPayload(payloadId: string) {
    await ingestionApi.reprocessPayload(payloadId);
    await refreshInbox();
  }

  async function refreshInbox() {
    await Promise.all([loadSummary(), loadQueues()]);
  }

  return {
    $reset,
    activeSourceId,
    aiPending,
    approveAll,
    approveFieldDrift,
    approveFieldsBatch,
    approveRouting,
    approveStructure,
    createSource,
    decisionCounts,
    error,
    failuresQueue,
    fieldDriftQueue,
    filteredFailures,
    filteredFieldDrift,
    filteredRouting,
    filteredStructure,
    isAllCaughtUp,
    isLoading,
    landing,
    loadFieldDriftQueue,
    loadQueues,
    loadRoutingQueue,
    loadSources,
    loadStructureQueue,
    loadSummary,
    refreshInbox,
    reprocessPayload,
    retryRecord,
    reviewByCampaign,
    routingQueue,
    setActiveSource,
    sources,
    sourcesNeedingMappingCount,
    structureQueue,
    summary,
    totalDecisionCount,
  };
});
